// src/hooks/useEmbedMode.ts
import { useEffect, useState } from 'react';

export interface EmbedModeConfig {
  isEmbedMode: boolean;
  skipFirebaseAuth: boolean;
  hideHeader: boolean;
  authToken: string | null;
}

/**
 * 埋め込みモードの設定を管理するフック
 */
export const useEmbedMode = (): EmbedModeConfig => {
  const [authToken, setAuthToken] = useState<string | null>(null);

  // URLパラメータから埋め込みモードを検出（Routerに依存しない）
  // useState with lazy initializationを使用（useMemoのSSR問題を回避）
  const [isEmbedMode] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    try {
      const params = new URLSearchParams(window.location.search);
      const embedParam = params.get('embed');
      const urlFlag = embedParam === 'true';

      // WebView環境かどうかを判定（mobile WebViewからのアクセスかチェック）
      const isWebView = /wv|WebView|Flutter/i.test(navigator.userAgent);

      // 永続化された埋め込みフラグ（WebView環境でのみ使用し、内部遷移でクエリが消えても維持）
      const persistedFlag = isWebView && window.localStorage.getItem('__embed_mode__') === 'true';

      // URLにembed=trueがあり、かつWebView環境であれば保存
      if (urlFlag && isWebView) {
        window.localStorage.setItem('__embed_mode__', 'true');
      }

      // 通常のブラウザの場合は、localStorage フラグをクリア
      if (!isWebView && window.localStorage.getItem('__embed_mode__')) {
        window.localStorage.removeItem('__embed_mode__');
      }

      return urlFlag || persistedFlag;
    } catch (e) {
      console.error('🔗 EmbedMode: URLパラメータ解析エラー', e);
      return false;
    }
  });

  useEffect(() => {
    if (isEmbedMode) {
      const embedStartTime = performance.now();

      let pollInterval: NodeJS.Timeout | null = null;
      let pollCount = 0;
      const MAX_POLL_COUNT = 50; // 最大5秒間（100ms * 50回）
      const pollStartTime = performance.now();

      // CustomTokenを取得する関数
      const extractCustomToken = () => {
        // WebView環境では、JavaScriptで window.__customToken に設定されたCustomTokenを取得
        const token = (window as any).__customToken || null;
        if (token) {
          const tokenFoundTime = performance.now();
          setAuthToken(token);
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
          return true;
        } else {
          if (pollCount === 0) {}
          return false;
        }
      };

      // 初回実行
      if (!extractCustomToken()) {
        // トークンが見つからない場合、ポーリング開始
        pollInterval = setInterval(() => {
          pollCount++;
          if (extractCustomToken() || pollCount >= MAX_POLL_COUNT) {
            if (pollInterval) {
              clearInterval(pollInterval);
              pollInterval = null;
            }
            if (pollCount >= MAX_POLL_COUNT) {
              const timeoutTime = performance.now();
              console.error('❌ EmbedMode: CustomToken取得タイムアウト');
              console.error(`⏱️ [PERF-WEB] タイムアウト: ${(timeoutTime - pollStartTime).toFixed(2)}ms経過`);
            }
          }
        }, 100); // 100msごとにチェック
      }

      // WebView側からのCustomToken設定イベントを待つ
      const handler = (e: Event) => {
        const eventTime = performance.now();
        const anyEvent = e as CustomEvent<{ token?: string }>;
        if (anyEvent?.detail?.token) {
          setAuthToken(anyEvent.detail.token || null);
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
        } else {
          extractCustomToken();
        }
      };
      window.addEventListener('embed-custom-token-set', handler);

      return () => {
        if (pollInterval) {
          clearInterval(pollInterval);
        }
        window.removeEventListener('embed-custom-token-set', handler);
      };
    }
  }, [isEmbedMode]);

  return {
    isEmbedMode,
    skipFirebaseAuth: isEmbedMode,
    hideHeader: isEmbedMode,
    authToken,
  };
};

/**
 * 埋め込みモード用のCustomTokenを設定する関数
 * WebViewから呼び出される
 */
export const setEmbedCustomToken = (token: string) => {
  (window as any).__customToken = token;

  // カスタムイベントを発火してコンポーネントに通知
  window.dispatchEvent(new CustomEvent('embed-custom-token-set', { detail: { token } }));
};

// グローバルに公開（WebViewから呼び出し可能にする）
if (typeof window !== 'undefined') {
  (window as any).setEmbedCustomToken = setEmbedCustomToken;
}
